import {Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {PageEvent} from '@angular/material/paginator';
import {finalize} from 'rxjs/operators';
import {UserNotificationService} from '../../services/notification/user-notification.service';
import {FolderService} from '../../services/folder/folder.service';
import {NotificationService} from '../../services/common/notification.service';
import {CurrentUserService} from '../../services/user/current-user.service';
import {NotificationType, UserNotification} from '../../dtos/notification/user-notification';
import {folderErrorMessage} from '../../shared/folder/folder-utils';
import {SettingsDialogComponent} from '../user/settings-dialog/settings-dialog.component';

const ICONS: { [type in NotificationType]: string } = {
  FOLDER_ITEM_CHANGED: 'edit_note',
  FOLDER_ITEM_REMOVED: 'delete_sweep',
  FOLDER_SHARED: 'folder_shared',
  FOLDER_SHARE_ACCEPTED: 'how_to_reg',
  FOLDER_SHARE_DECLINED: 'person_off',
  FOLDER_SHARE_LEFT: 'person_remove',
};

/**
 * the in-app notifications (Benachrichtigungen) of the user
 */
@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
  standalone: false
})
export class NotificationsComponent implements OnInit {

  notifications: UserNotification[] = [];
  totalElements = 0;
  unreadCount = 0;
  unreadOnly = false;
  pageIndex = 0;
  pageSize = 20;
  loading = true;

  constructor(private userNotificationService: UserNotificationService,
              private folderService: FolderService,
              private currentUserService: CurrentUserService,
              private notificationService: NotificationService,
              private router: Router,
              private dialog: MatDialog) {
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.userNotificationService.getNotifications(this.unreadOnly, this.pageIndex, this.pageSize)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: page => {
          // e.g. after deleting the last notification of the last page
          if (page.content.length === 0 && this.pageIndex > 0 && page.totalElements > 0) {
            this.pageIndex = Math.max(0, Math.ceil(page.totalElements / this.pageSize) - 1);
            this.load();
            return;
          }
          this.notifications = page.content;
          this.totalElements = page.totalElements;
          this.unreadCount = page.unreadCount;
        },
        error: error => this.notificationService.openSnackBar(folderErrorMessage(error))
      });
  }

  openSettings(): void {
    this.dialog.open(SettingsDialogComponent);
  }

  setUnreadOnly(unreadOnly: boolean): void {
    this.unreadOnly = unreadOnly;
    this.pageIndex = 0;
    this.load();
  }

  page(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.load();
  }

  icon(notification: UserNotification): string {
    return ICONS[notification.type] ?? 'notifications';
  }

  /** folder notifications link to the folder, but only paid users and admins can open folders */
  hasFolderLink(notification: UserNotification): boolean {
    return notification.folderId != null && this.currentUserService.canUseFolders();
  }

  markRead(notification: UserNotification): void {
    if (notification.read) {
      return;
    }
    this.userNotificationService.markRead(notification.id).subscribe({
      next: () => {
        notification.read = true;
        this.unreadCount = Math.max(0, this.unreadCount - 1);
        this.userNotificationService.unreadCount$.next(this.unreadCount);
        if (this.unreadOnly) {
          this.load();
        }
      },
      error: error => this.notificationService.openSnackBar(folderErrorMessage(error))
    });
  }

  markAllRead(): void {
    this.userNotificationService.markAllRead().subscribe({
      next: () => this.load(),
      error: error => this.notificationService.openSnackBar(folderErrorMessage(error))
    });
  }

  delete(notification: UserNotification): void {
    this.userNotificationService.delete(notification.id).subscribe({
      next: () => this.load(),
      error: error => this.notificationService.openSnackBar(folderErrorMessage(error))
    });
  }

  /**
   * the folder may have been deleted or unshared since the notification was created, so it is
   * checked before navigating
   */
  openFolder(notification: UserNotification): void {
    this.markRead(notification);
    this.folderService.getFolder(notification.folderId).subscribe({
      next: folder => this.router.navigate(['/sammelmappen', folder.id]),
      error: error => this.notificationService.openSnackBar(folderErrorMessage(error, {
        403: 'Sie haben keinen Zugriff mehr auf die Sammelmappe „' + notification.folderName + '“.',
        404: 'Die Sammelmappe „' + notification.folderName + '“ existiert nicht mehr.'
      }))
    });
  }
}
